//
// gleason.ts — the projection
//
// Translation of `Utils/GleasonProjection.swift`, which was itself a 1:1
// port of the original Fuser app's `src/utils/gleasonProjection.ts`. So
// this is, in effect, that original file returning home — and it must stay
// mathematically identical to the Swift, because the two apps have to put
// the same aircraft over the same country.
//
// Gleason Azimuthal Equidistant Polar Projection, centred on the North
// Pole (90°N, 0°E/W). Co-latitude maps linearly to radius: the pole is the
// centre, the equator is a circle at half the radius, the South Pole is the
// outer rim.
//

export interface Point {
  x: number;
  y: number;
}

/** A ring of coordinates in GeoJSON [lon, lat] order. */
export type Ring = Array<[number, number]>;

/**
 * Projects (lat, lon) onto the polar disc.
 *
 * @param mapRadius radius of the full Earth disc — North Pole to South Pole.
 */
export function project(
  lat: number,
  lon: number,
  centerX: number,
  centerY: number,
  mapRadius: number,
): Point {
  // Co-latitude: 0 at the North Pole, 90 at the equator, 180 at the South.
  const coLat = 90 - lat;
  const r = (coLat / 180) * mapRadius;

  // 0° Greenwich points straight down. Longitude is negated so west
  // (the Americas) sits to the left — the Gleason disc. Dave, 2026-08-22:
  // "the map is backwards." The old formula used +lon and put America on
  // the right.
  const angleRad = (-lon * Math.PI) / 180 + Math.PI / 2;

  return {
    x: centerX + r * Math.cos(angleRad),
    y: centerY + r * Math.sin(angleRad),
  };
}

/**
 * Canvas coordinates back to (lat, lon), or null outside the disc — with
 * the same 5% slop as the original, so a tap just past the rim still counts.
 */
export function unproject(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  mapRadius: number,
): { lat: number; lon: number } | null {
  const dx = x - centerX;
  const dy = y - centerY;
  const dist = Math.hypot(dx, dy);
  if (dist > mapRadius * 1.05) return null;

  const coLat = (dist / mapRadius) * 180;
  const lat = 90 - coLat;

  const angleRad = Math.atan2(dy, dx);
  let lon = -((angleRad - Math.PI / 2) * 180) / Math.PI;
  while (lon < -180) lon += 360;
  while (lon > 180) lon -= 360;

  return { lat, lon };
}

/**
 * Traces a ring of (lon, lat) coordinates into a canvas path, subdividing
 * any step wider than 4° into interpolated points.
 *
 * The subdivision is not cosmetic. On an azimuthal projection a straight
 * screen line between two widely separated coordinates cuts a chord across
 * the disc — a coastline drawn without it slices through the pole or the
 * middle of an ocean. This is the `GleasonProjection.path(...)` behaviour
 * the Swift comments flag as tightly coupled to the drawing code, and it
 * is reproduced exactly.
 */
export function tracePath(
  ctx: CanvasRenderingContext2D,
  coordinates: Ring,
  centerX: number,
  centerY: number,
  mapRadius: number,
  closed = true,
): void {
  if (coordinates.length < 2) return;

  const points: Point[] = [];

  for (let i = 0; i < coordinates.length; i++) {
    const [currLon, currLat] = coordinates[i];
    const [nextLon, nextLat] = coordinates[(i + 1) % coordinates.length];

    points.push(project(currLat, currLon, centerX, centerY, mapRadius));

    if (!closed && i === coordinates.length - 1) break;

    let dLon = nextLon - currLon;
    while (dLon > 180) dLon -= 360;
    while (dLon < -180) dLon += 360;
    const dLat = nextLat - currLat;
    const distance = Math.hypot(dLon, dLat);

    const steps = Math.ceil(distance / 4);
    if (steps > 1) {
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        points.push(
          project(currLat + dLat * t, currLon + dLon * t, centerX, centerY, mapRadius),
        );
      }
    }
  }

  if (points.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  if (closed) ctx.closePath();
}

/**
 * Traces an open track of (lat, lon) points — ground tracks, terminator
 * arcs, sun and moon paths.
 *
 * **Subdivides, as of build 1.5 item 4.** It used to join the points with
 * straight screen lines and nothing else, which is the exact mistake
 * `tracePath` above exists to avoid and warns about in its own comment: on
 * an azimuthal projection a straight screen line between two widely
 * separated coordinates cuts a *chord* across the disc rather than
 * following the curve.
 *
 * Satellite ground tracks were sampled about 12 degrees apart, so every
 * trail was thirty chords slicing over the pole and the open ocean — what
 * Dave saw as "the path… rendering in too many places at once". The
 * coastlines never had the problem because they went through `tracePath`.
 *
 * Same 4-degree rule and the same interpolation as `tracePath`, so the two
 * cannot drift apart.
 */
export function traceTrack(
  ctx: CanvasRenderingContext2D,
  points: Array<{ lat: number; lon: number }>,
  centerX: number,
  centerY: number,
  mapRadius: number,
): void {
  if (points.length < 2) return;

  const screen: Point[] = [];

  for (let i = 0; i < points.length; i++) {
    const curr = points[i];
    screen.push(project(curr.lat, curr.lon, centerX, centerY, mapRadius));

    // Open track: the last point has nothing to reach toward.
    if (i === points.length - 1) break;

    const next = points[i + 1];
    let dLon = next.lon - curr.lon;
    while (dLon > 180) dLon -= 360;
    while (dLon < -180) dLon += 360;
    const dLat = next.lat - curr.lat;
    const distance = Math.hypot(dLon, dLat);

    const steps = Math.ceil(distance / 4);
    if (steps > 1) {
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        screen.push(
          project(curr.lat + dLat * t, curr.lon + dLon * t, centerX, centerY, mapRadius),
        );
      }
    }
  }

  ctx.beginPath();
  ctx.moveTo(screen[0].x, screen[0].y);
  for (let i = 1; i < screen.length; i++) ctx.lineTo(screen[i].x, screen[i].y);
}
